import { ReactElement } from 'react';
import { TextKey } from '@mews-commander/enums';
import { LocalizedText, lt, showSuccessAlert, useDialogAction } from '@mews/framework';
import { Button, Dialog } from '@optimus-web/core';
import { ExportTypeEnum, useRepositoryMutation } from '@mews-commander/data-access';
import { useCurrentEnterpriseId } from '@mews-commander/core';

interface DeleteScheduleProps {
    scheduleExportId: string;
    exportName: string;
    scheduleExportType: ExportTypeEnum;
    renderButtonComponent: (onClick: () => void) => ReactElement;
}
export interface DeleteScheduleParams {
    scheduleExportId: string;
    scheduleExportType: ExportTypeEnum;
}
export const useDeleteScheduleExport = ({ onSuccess }: { onSuccess: () => void }) => {
    const enterpriseId = useCurrentEnterpriseId();
    return useRepositoryMutation('exports', 'deleteSchedule', {
        transformParams: (param: DeleteScheduleParams) => ({
            enterpriseId,
            scheduleExportId: param.scheduleExportId,
            scheduleExportType: param.scheduleExportType,
        }),
        invalidateQueries: {
            exports: ['searchSchedules'],
        },
        onSuccess,
    });
};

export const DeleteSchedule = ({
    scheduleExportId,
    exportName,
    scheduleExportType,
    renderButtonComponent,
}: DeleteScheduleProps) => {
    const { mutateAsync } = useDeleteScheduleExport({
        onSuccess: () => {
            showSuccessAlert({ description: lt(TextKey.ChangesSuccessfullySaved) });
        },
    });

    const deleteAction = useDialogAction(async () => mutateAsync({ scheduleExportId, scheduleExportType }));

    return (
        <>
            {renderButtonComponent(deleteAction.handlers.handleOpen)}
            {deleteAction.states.isDialogOpened ? (
                <Dialog
                    title={<LocalizedText textKey={TextKey.DeleteConfirmation} substitutes={{ entity: exportName }} />}
                    footer={{
                        primary: (
                            <Button
                                size="medium"
                                variant="destructive"
                                onClick={deleteAction.handlers.handleConfirm}
                                type="button"
                            >
                                {lt(TextKey.Delete)}
                            </Button>
                        ),
                        secondary: (
                            <Button
                                size="medium"
                                variant="tertiary"
                                onClick={deleteAction.handlers.handleCancel}
                                type="button"
                            >
                                {lt(TextKey.GoBack)}
                            </Button>
                        ),
                    }}
                    active
                    loading={deleteAction.states.isProcessing}
                    onClose={deleteAction.handlers.handleCancel}
                >
                    {lt(TextKey.DeleteHint)}
                </Dialog>
            ) : null}
        </>
    );
};
